import React, { useEffect, useRef, useState } from 'react'

import translationKeys from '@translationKeys'

import { ProfileType } from '@modules/search/types'

import { getUpdatesLink } from '../../constants'
import {
  UpdatesCell,
  UpdatesCellCropped,
  UpdatesCellHighlightedText,
  UpdatesCellMainText,
  UpdatesCellText,
} from '../../index.styles'
import {
  BIOTECH_UPDATE_KEY_NAME_MAP,
  MEDTECH_UPDATE_KEY_NAME_MAP,
  SERVICES_UPDATE_KEY_NAME_MAP,
  TECHNOLOGIES_UPDATE_KEY_NAME_MAP,
  THREE_ROWS_MINIMUM_HEIGHT,
  UPDATE_KEY_NAME_MAP,
} from '../../types'
import { convertHtmlToText } from '../../utils'
import { UpdatesCellContentProps } from './types'

const getUpdatesMap = (profileType: ProfileType) => {
  if (profileType === ProfileType.biotech) {
    return BIOTECH_UPDATE_KEY_NAME_MAP
  }
  if (profileType === ProfileType.medtech) {
    return MEDTECH_UPDATE_KEY_NAME_MAP
  }
  if (profileType === ProfileType.services) {
    return SERVICES_UPDATE_KEY_NAME_MAP
  }
  if (profileType === ProfileType.technologies) {
    return TECHNOLOGIES_UPDATE_KEY_NAME_MAP
  }
  return UPDATE_KEY_NAME_MAP
}

const UpdatesCellContent = ({ profileType, data, t, profileId }: UpdatesCellContentProps) => {
  const ref = useRef(null)
  const [height, setHeight] = useState<number>(0)

  const updatesMap = getUpdatesMap(profileType)
  const entries = data.map(item => Object.entries(item)).flat()

  const objects = entries.map(entry => ({
    key: entry?.[0],
    value: entry?.[1],
  }))

  useEffect(() => {
    //  @ts-expect-error TODO: fix typing
    setHeight(ref?.current?.clientHeight)
    // @ts-expect-error TODO: fix typing
  }, [ref?.current?.clientHeight])

  const mergedObjects = Array.from(
    objects.reduce(
      (m, { key, value }) =>
        // @ts-expect-error TODO: fix typing
        m.set(key, (m.get(key) || (key === 'addedAssetsCount' ? 0 : '')) + (value?.name ? `${value?.name}, ` : value)),
      new Map(),
    ),
    ([key, value]) => ({ key, value }),
  )

  const showMoreButton = height >= THREE_ROWS_MINIMUM_HEIGHT

  const displayedData = mergedObjects

  const getStartUpdatesText = (key: string, idx: number) => {
    if (key === 'newEvent') {
      return <UpdatesCellText isLowercase={idx !== 0}>{t(translationKeys.REGISTERED_FOR)}</UpdatesCellText>
    }

    if (key === 'corporateHistoryUpdated') {
      return <UpdatesCellMainText isLowercase={idx !== 0}>{t(translationKeys.CORPORATE_HISTORY)}</UpdatesCellMainText>
    }

    if (key === 'addedAssetsCount') {
      return <UpdatesCellMainText isLowercase={idx !== 0}>{t(translationKeys.ADDED)}</UpdatesCellMainText>
    }

    if (
      key === 'investmentTimeframe' ||
      key === 'devPhase' ||
      (key === 'differentiator' && profileType === ProfileType.biotech)
    ) {
      return <UpdatesCellText isLowercase={idx !== 0}>{t(translationKeys.CHANGED)}</UpdatesCellText>
    }

    return <UpdatesCellText isLowercase={idx !== 0}>{t(translationKeys.UPDATED)}</UpdatesCellText>
  }

  const getSecondUpdatesText = (key: string) => {
    if (key === 'corporateHistoryUpdated') {
      return <UpdatesCellText isLowercase>{t(translationKeys.UPDATED_WITH)}</UpdatesCellText>
    }

    if (key === 'addedAssetsCount') {
      return null
    }

    if (key === 'newEvent') {
      return <UpdatesCellMainText>{updatesMap[key] || key}:</UpdatesCellMainText>
    }

    // @ts-expect-error TODO: fix typing
    return <UpdatesCellMainText>{updatesMap[key] || key}</UpdatesCellMainText>
  }

  const getThirdUpdatesText = (key: string) => {
    if (['newEvent', 'corporateHistoryUpdated'].includes(key)) {
      return null
    }

    if (key === 'addedAssetsCount') {
      return null
    }

    if (
      [
        'sector',
        'addedSubSectors',
        'addedPrimaryTherapeuticAreas',
        'categorySector',
        'addedCategorySubSectors',
        'addedServiceAvailability',
      ].includes(key)
    ) {
      return <UpdatesCellText>{t(translationKeys.TO_INCLUDE)}</UpdatesCellText>
    }

    return <UpdatesCellText>{t(translationKeys.TO)}</UpdatesCellText>
  }

  const getMainUpdatesText = (key: string, value: { name: string } | string) => {
    if (key === 'addedAssetsCount') {
      return `${value} ${Number(value) > 1 ? t(translationKeys.NEW_ASSETS) : t(translationKeys.NEW_ASSET)}`
    }

    if (Array.isArray(value)) {
      return value.join(', ')
    }

    if (key === 'corporateHistoryUpdated') {
      return t(translationKeys.NEW_ACTIVITY)
    }

    if (key === 'newEvent') {
      // @ts-expect-error TODO: fix typing
      const trimmedValue = value.slice(0, -2)
      return convertHtmlToText(trimmedValue)
    }

    // @ts-expect-error fix-typing
    return convertHtmlToText(value)
  }
  const updates = displayedData.map((item, idx) => (
    <>
      {getStartUpdatesText(item.key, idx)}
      {getSecondUpdatesText(item.key)}
      {getThirdUpdatesText(item.key)}
      <UpdatesCellHighlightedText isClickable href={getUpdatesLink({ key: item.key, id: profileId, type: profileType })}>
        {getMainUpdatesText(item.key, item.value)}

        {idx !== (displayedData?.length || 0) - 1 ? ',' : null}
      </UpdatesCellHighlightedText>
    </>
  ))

  const updatesContent = <UpdatesCell ref={ref}>{updates}</UpdatesCell>

  if (!showMoreButton) {
    return <UpdatesCell>{updatesContent}</UpdatesCell>
  }

  return (
    <UpdatesCell>
      <UpdatesCellCropped>{updatesContent}</UpdatesCellCropped>
      <>
        <UpdatesCellText>...</UpdatesCellText>
        <UpdatesCellHighlightedText
          isClickable
          href={getUpdatesLink({ key: mergedObjects?.[2]?.key, id: profileId, type: profileType })}
        >
          {t(translationKeys.MORE)}
        </UpdatesCellHighlightedText>
      </>
    </UpdatesCell>
  )
}

export default UpdatesCellContent
